/**
 * Canonical Word & Brown medical benefit fields, in display order, grouped.
 *
 * Derived from the actual benefit lines W&B returns (mined from real plan data),
 * so the plan-benefits editor matches the carrier's fields one-to-one instead of
 * a generic deductible/copay block. `name` is the exact W&B line name, so it maps
 * directly to a `rawBenefits` entry ({ name, value, network }).
 *
 * Values are free text ("$20 Copay", "No Charge", "40% after deductible") — W&B
 * does not return clean numbers. `split` fields arrive combined as
 * "<individual> / <family>" and are edited as two boxes.
 */

export interface WbBenefitField {
  /** Exact W&B benefit line name — matches rawBenefits[].name. */
  name: string;
  /** Human label shown in the editor. */
  label: string;
  /** Value is "individual / family" — render as two boxes. */
  split?: boolean;
}

export interface WbBenefitGroup {
  group: string;
  fields: WbBenefitField[];
}

export const WB_MEDICAL_BENEFIT_FIELDS: WbBenefitGroup[] = [
  {
    group: "Cost shares",
    fields: [
      { name: "Deductible (Individual/Family)", label: "Deductible", split: true },
      { name: "Out-of-Pocket (Individual/Family)", label: "Out-of-pocket max", split: true },
    ],
  },
  {
    group: "Office visits",
    fields: [
      { name: "Primary Care Visit", label: "Primary care visit" },
      { name: "Specialist Visit", label: "Specialist visit" },
      { name: "Other Practitioner Visit", label: "Other practitioner visit" },
      { name: "Preventive Care/Screening/Immunizations", label: "Preventive care" },
      { name: "Urgent Care", label: "Urgent care" },
    ],
  },
  // DEV-348: SBCs list lab work and imaging separately from office visits.
  // Not W&B line names (W&B returns none of these); like every name here they
  // are storage keys in plans.raw_benefits, so never rename one once shipped.
  {
    group: "Tests & imaging",
    fields: [
      { name: "Laboratory Services (Blood Work)", label: "Lab (blood work)" },
      { name: "Basic Imaging (X-Ray)", label: "Imaging — basic (x-ray)" },
      { name: "Advanced Imaging (CT/PET Scans, MRIs)", label: "Imaging — advanced (CT/PET/MRI)" },
    ],
  },
  {
    group: "Emergency",
    fields: [
      { name: "Emergency Room Services", label: "Emergency room" },
      { name: "Emergency Medical Transportation", label: "Emergency transportation" },
    ],
  },
  {
    group: "Hospital & surgery",
    fields: [
      { name: "Facility Fee (Hospital Room)", label: "Hospital — facility fee" },
      { name: "Physician/Surgeon Fee (Hospital Room)", label: "Hospital — physician fee" },
      { name: "Facility Fee (Ambulatory Surgery Center)", label: "Surgery center — facility fee" },
      { name: "Physician/Surgeon Fee (Ambulatory Surgery Center)", label: "Surgery center — physician fee" },
    ],
  },
  {
    group: "Mental health & substance use",
    fields: [
      { name: "Mental/Behavioral Health - Inpatient", label: "Mental health — inpatient" },
      { name: "Substance Use Disorder - Inpatient", label: "Substance use — inpatient" },
      { name: "Substance Use Disorder - Outpatient", label: "Substance use — outpatient" },
    ],
  },
  {
    group: "Maternity",
    fields: [
      { name: "Delivery and Inpatient Services - Hospital", label: "Delivery — hospital" },
      { name: "Delivery and Inpatient Services - Professional", label: "Delivery — professional" },
    ],
  },
  {
    group: "Prescriptions",
    fields: [
      { name: "Rx – Tier 1", label: "Rx Tier 1" },
      { name: "Rx – Tier 2", label: "Rx Tier 2" },
      { name: "Rx – Tier 3", label: "Rx Tier 3" },
      { name: "Rx – Tier 4", label: "Rx Tier 4" },
    ],
  },
  // DEV-348: a second pharmacy/Rx level some carriers file on the SBC (e.g.
  // Blue Shield's "Rx Level B"). Shown in plan details; quote summaries keep
  // reading the first level above.
  {
    group: "Prescriptions — Level B",
    fields: [
      { name: "Rx Level B – Tier 1", label: "Rx Level B Tier 1" },
      { name: "Rx Level B – Tier 2", label: "Rx Level B Tier 2" },
      { name: "Rx Level B – Tier 3", label: "Rx Level B Tier 3" },
      { name: "Rx Level B – Tier 4", label: "Rx Level B Tier 4" },
    ],
  },
];
